import { SeoHead } from "@/components/common/SeoHead";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { useEffect } from "react";

export default function TermsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div id="main-content" className="min-h-screen bg-white" data-testid="terms-page">
      <SeoHead
        title="Terms of Service"
        description="Read the Tripsyfy terms of service — the terms and conditions governing use of our website and travel services."
        canonical="https://tripsyfy.com/terms"
        noIndex
      />
      <Navbar />

      <section className="pt-24 lg:pt-28 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="heading-xl text-primary mb-4">Terms of Service</h1>
          <p className="text-sm text-muted-foreground mb-10">Last updated: April 7, 2026</p>

          <div className="prose prose-sm max-w-none text-muted-foreground space-y-6">

            <p>Welcome to Tripsyfy. These Terms of Service ("Terms") govern your use of the Tripsyfy website and any inquiries you submit through it. By using our website, you agree to these Terms.</p>

            <h2 className="text-lg font-bold text-primary mt-8 mb-4">1. Use of Our Website</h2>
            <p>Our website is provided for general informational purposes and to allow users to contact us regarding travel-related services.</p>
            <p>You agree to use this website only for lawful purposes and in a way that does not harm, disrupt, or interfere with the website or our services.</p>

            <h2 className="text-lg font-bold text-primary mt-8 mb-4">2. No Booking Guarantee</h2>
            <p>Submitting an inquiry through our website does not create a confirmed booking, reservation, or contract for travel services.</p>
            <p>Any travel booking, reservation, itinerary, or related service will only be confirmed separately by Tripsyfy and, where applicable, will be subject to additional terms from the relevant travel supplier.</p>

            <h2 className="text-lg font-bold text-primary mt-8 mb-4">3. Accuracy of Information</h2>
            <p>You agree to provide accurate and complete information when submitting travel inquiries. We are not responsible for issues caused by inaccurate, incomplete, or outdated information provided by you.</p>

            <h2 className="text-lg font-bold text-primary mt-8 mb-4">4. Quotes and Availability</h2>
            <p>Any quotes, pricing, travel options, and availability shared by Tripsyfy are subject to change until confirmed. Prices, availability, and travel conditions may change at any time based on supplier policies, market conditions, and timing.</p>

            <h2 className="text-lg font-bold text-primary mt-8 mb-4">5. Third-Party Travel Suppliers</h2>
            <p>Tripsyfy may assist you with travel services involving third-party suppliers such as airlines, hotels, cruise lines, tour operators, transportation providers, and other travel-related partners.</p>
            <p>These third-party suppliers operate independently and may have their own terms, conditions, cancellation policies, refund rules, and travel requirements. Tripsyfy is not responsible for the acts, omissions, errors, delays, cancellations, overbookings, policy changes, or service failures of third-party suppliers.</p>

            <h2 className="text-lg font-bold text-primary mt-8 mb-4">6. Traveler Responsibility</h2>
            <p>You are responsible for reviewing and meeting all travel requirements applicable to your trip, including passport validity, visas, entry rules, health requirements, insurance needs, and any other destination or supplier requirements.</p>
            <p>Tripsyfy is not responsible for losses or disruptions caused by your failure to meet such requirements.</p>

            <h2 className="text-lg font-bold text-primary mt-8 mb-4">7. Cancellations and Refunds</h2>
            <p>Any cancellation, refund, change, or credit related to travel services is subject to the terms and policies of the applicable travel supplier and any separate written terms provided by Tripsyfy at the time of booking.</p>

            <h2 className="text-lg font-bold text-primary mt-8 mb-4">8. Intellectual Property</h2>
            <p>All content on this website, including text, branding, graphics, logos, and design elements, is owned by or used with permission by Tripsyfy and may not be copied, reproduced, distributed, or used without prior written permission.</p>

            <h2 className="text-lg font-bold text-primary mt-8 mb-4">9. Disclaimer</h2>
            <p>The information on this website is provided on an "as is" and "as available" basis. While we try to keep information accurate and up to date, we do not guarantee that the website will always be error-free, complete, current, or uninterrupted.</p>

            <h2 className="text-lg font-bold text-primary mt-8 mb-4">10. Limitation of Liability</h2>
            <p>To the fullest extent permitted by law, Tripsyfy will not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or related to your use of the website or any travel services arranged through third-party suppliers.</p>

            <h2 className="text-lg font-bold text-primary mt-8 mb-4">11. Indemnification</h2>
            <p>You agree to indemnify and hold harmless Tripsyfy, its owners, affiliates, partners, and representatives from any claims, losses, liabilities, damages, or expenses arising from your misuse of the website, your violation of these Terms, or your breach of any applicable law or third-party rights.</p>

            <h2 className="text-lg font-bold text-primary mt-8 mb-4">12. Governing Law</h2>
            <p>These Terms are governed by the laws of the State of New Jersey, without regard to conflict of law principles.</p>

            <h2 className="text-lg font-bold text-primary mt-8 mb-4">13. Changes to These Terms</h2>
            <p>We may update these Terms from time to time. Any changes will be posted on this page with a revised "Last updated" date. Your continued use of the website after changes are posted means you accept the updated Terms.</p>

            <h2 className="text-lg font-bold text-primary mt-8 mb-4">14. Contact Us</h2>
            <p>If you have any questions about these Terms, please contact us at:</p>
            <ul className="list-none space-y-1 mt-3">
              <li className="font-medium text-primary">Tripsyfy</li>
              <li>Edison, New Jersey, USA</li>
              <li>Email: <a href="mailto:hello@tripsyfy.com" className="text-accent hover:underline">hello@tripsyfy.com</a></li>
              <li>Phone: <a href="tel:+19726661122" className="text-accent hover:underline">+1 972-666-1122</a></li>
            </ul>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
